import DynamicImage from '@/components/commons/dynamic-image';
import { Badge } from '@/components/ui/badge';
import { cn, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType } from '@/stores/builder';
import { BaseFreeGift } from '@/stores/promotion';
import { BasePromotionRewardProduct } from '@/types/http/promotion/common';
import { Dispatch, SetStateAction } from 'react';

type CardGiftProductProps = {
  colorScheme?: ThemeColorSchemeType;
  product: BasePromotionRewardProduct;
  selectedProducts: BaseFreeGift[];
  setSelectedProduct: Dispatch<SetStateAction<BasePromotionRewardProduct | undefined>>;
  setSelectedTab: Dispatch<SetStateAction<'product_group' | 'product_variant'>>;
};

export default function CardGiftProduct({
  colorScheme,
  product,
  selectedProducts,
  setSelectedProduct,
  setSelectedTab,
}: CardGiftProductProps) {
  const handleSelectProductGroup = () => {
    if (product.stock === 0) return;
    setSelectedProduct(product);
    setSelectedTab('product_variant');
  };

  const isSelected = selectedProducts.some(
    (p) => p.product_group.product_group_id === product.product_group_id
  );

  return (
    <div
      className={cn(
        'relative flex w-full cursor-pointer flex-col rounded-lg border transition-all duration-200 ease-in-out',
        isSelected
          ? isEmpty(colorScheme)
            ? 'border-primary'
            : 'border-[var(--card-background)]'
          : 'border-gray-200',
        product.stock === 0 && 'cursor-not-allowed'
      )}
      style={
        {
          '--card-background': colorScheme?.background,
          '--text-primary': colorScheme?.text,
          '--text-secondary': colorScheme?.textSecondary,
        } as React.CSSProperties
      }
      onClick={handleSelectProductGroup}
    >
      <div className='w-full'>
        <DynamicImage
          src={(product?.product_assets?.[0] as string) ?? ''}
          alt='product gift'
          width={500}
          height={500}
          className={cn(
            'aspect-square max-h-[200px] w-full rounded-t-lg object-cover',
            product.stock === 0 && 'grayscale'
          )}
        />
        {product.stock === 0 && (
          <Badge variant='secondary' className='absolute bottom-2 left-2 font-normal'>
            Sold Out
          </Badge>
        )}
      </div>

      <div className='line-clamp-1 flex h-full w-full flex-col justify-between gap-2 rounded-b-lg px-3.5 pb-2 pt-3'>
        <div className='flex flex-col gap-0.5'>
          <h4
            className={cn(
              'truncate text-sm font-bold text-[--text-primary]',
              product.stock === 0 && 'text-gray-300'
            )}
          >
            {product.product_name}
          </h4>
          <span
            className={cn(
              'line-clamp-1 text-xs text-[--text-secondary]',
              product.stock === 0 && 'text-gray-300'
            )}
          >
            {product.total_variants} Variant
          </span>
        </div>
      </div>
    </div>
  );
}
